import {
  forwardRef,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MultipleSelectOptionData } from './multiple-select.interface';

declare let jQuery: any;

@Component({
  selector: 'app-multiple-select',
  templateUrl: './multiple-select.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultipleSelectComponent),
      multi: true,
    },
  ],
})
export class MultipleSelectComponent implements AfterViewInit, OnChanges, OnDestroy, DoCheck, ControlValueAccessor {
  @ViewChild('selector', { static: true }) selector: ElementRef;

  @Input() data: MultipleSelectOptionData[];
  @Input() placeholder = '';
  @Input() dropdownParent = '';
  @Input() allowClear = false;
  @Input() value: string | string[];
  @Input() width: string;
  @Input() disabled = false;
  @Input() options: any;
  @Output() valueChanged = new EventEmitter<string | string[]>();

  private element: any = undefined;
  private check = false;

  constructor(private renderer: Renderer2, public zone: NgZone, public _element: ElementRef) {
  }

  ngDoCheck() {
    if (!this.element) {
      return;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.element) {
      return;
    }

    if (changes['data'] && JSON.stringify(changes['data'].previousValue) !== JSON.stringify(changes['data'].currentValue)) {
      this.initPlugin();

      const newValue: string | string[] = this.value;
      this.setElementValue(newValue);
      this.valueChanged.emit(newValue);
      this.propagateChange(newValue);
    }

    if (changes['value'] && changes['value'].previousValue !== changes['value'].currentValue) {
      const newValue: string = changes['value'].currentValue;

      this.setElementValue(newValue);
      this.valueChanged.emit(newValue);
      this.propagateChange(newValue);
    }

    if (changes['disabled'] && changes['disabled'].previousValue !== changes['disabled'].currentValue) {
      this.renderer.setProperty(this.selector.nativeElement, 'disabled', this.disabled);
    }

    if (changes['placeholder'] && changes['placeholder'].previousValue !== changes['placeholder'].currentValue) {
      this.element.data('select2').$container.find('.select2-selection__placeholder').text(this.placeholder);
    }

    if (changes['dropdownParent'] && changes['dropdownParent'].previousValue !== changes['dropdownParent'].currentValue) {
      this.renderer.setAttribute(this.selector.nativeElement, 'data-dropdownParent', this.dropdownParent);
    }

    if (changes['allowClear'] && changes['allowClear'].previousValue !== changes['allowClear'].currentValue) {
      this.renderer.setAttribute(this.selector.nativeElement, 'data-allow-clear', this.allowClear.toString());
    }
  }

  ngAfterViewInit() {
    setTimeout(
      () => {
        this.element = jQuery(this.selector.nativeElement);

        this.renderer.setAttribute(this.selector.nativeElement, 'data-dropdownParent', this.dropdownParent);
        this.renderer.setAttribute(this.selector.nativeElement, 'data-allow-clear', this.allowClear.toString());

        this.initPlugin();

        if (typeof this.value !== 'undefined') {
          this.setElementValue(this.value);
        }

        this.element.on('select2:select select2:unselect', (e: any) => {
          const newValue = this.element.val();

          this.valueChanged.emit(newValue);
          this.propagateChange(newValue);
        });
      },
      100);
  }

  ngOnDestroy() {
    if (this.element) {
      this.element.off('select2:select');
    }
  }

  private initPlugin() {
    if (!this.element.select2) {
      if (!this.check) {
        this.check = true;
        console.log('Please add Select2 library (js file) to the project.' +
          'You can download it from https://github.com/select2/select2/tree/master/dist/js.');
      }

      return;
    }

    // If select2 already initialized remove him and remove all tags inside
    if (this.element.hasClass('select2-hidden-accessible') === true) {
      this.element.select2('destroy');
      this.renderer.setProperty(this.selector.nativeElement, 'innerHTML', '');
    }

    let options: any = {
      data: this.data,
      width: (this.width) ? this.width : 'resolve',
      placeholder: this.placeholder
    };

    if (this.dropdownParent) {
      options = {
        data: this.data,
        width: (this.width) ? this.width : 'resolve',
        dropdownParent: jQuery(`#${this.dropdownParent}`),
      };
    }

    Object.assign(options, this.options);

    if (options.matcher) {
      jQuery.fn.select2.amd.require(['select2/compat/matcher'], (oldMatcher: any) => {
        options.matcher = oldMatcher(options.matcher);
        this.element.select2(options);

        if (typeof this.value !== 'undefined') {
          this.setElementValue(this.value);
        }
      });
    } else {
      this.element.select2(options);
    }

    this.renderer.setProperty(this.selector.nativeElement, 'disabled', this.disabled);
  }

  private setElementValue(newValue: string | string[]) {
    if (Array.isArray(newValue)) {

      for (const option of this.selector.nativeElement.options) {
        this.renderer.setProperty(option, 'selected', (newValue.indexOf(option.value) > -1));
      }
    } else {
      this.renderer.setProperty(this.selector.nativeElement, 'value', newValue);
    }

    if (this.element) {
      this.element.trigger('change.select2');
    }
  }

  writeValue(value: any) {
    if (value !== undefined) {
      this.value = value;
      this.setElementValue(value);
    }
  }

  propagateChange = (value: string | string[]) => { };

  registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  registerOnTouched() {}

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    this.renderer.setProperty(this.selector.nativeElement, 'disabled', this.disabled);
  }
}
